import React, { useEffect, useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users as UsersIcon, BookOpen, Radio, MessageSquare, FileText, UserCog,
  BarChart3, Trash2, Pencil, Save, X, KeyRound, Plus, Search, ShieldAlert, LogOut, Mail,
  CreditCard, UserPlus, Check, Ban, GraduationCap, Eye, AlertCircle, Settings as SettingsIcon,
  ClipboardList, Download, Upload, HelpCircle, Timer, MessageCircle, Send, Award,
} from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { CertificatesTab } from "@/components/admin/CertificatesTab";
import { ResultsOverview } from "@/components/admin/ResultsOverview";
import { EmailLogTab } from "@/components/admin/EmailLogTab";
import { GradingQueue } from "@/components/admin/GradingQueue";
import { CohortRegistrationTab } from "@/components/admin/CohortRegistrationTab";
import { ProposalsTab } from "@/components/admin/ProposalsTab";
import { AmbassadorsTab } from "@/components/admin/AmbassadorsTab";
import { InviteAmbassadorButton } from "@/components/admin/InviteAmbassadorButton";
import { PortfolioPassButton, PortfolioInviteButton } from "@/components/admin/PortfolioPassButton";
import { FacultyApplications } from "@/components/admin/FacultyApplications";
import { StudyGuidesTab } from "@/components/admin/StudyGuidesTab";

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "faculty", label: "Faculty", icon: UserCog },
  { key: "courses", label: "Courses", icon: BookOpen },
  { key: "live", label: "Live sessions", icon: Radio },
  { key: "enrolments", label: "Enrolments", icon: GraduationCap },
  { key: "exams", label: "Exams", icon: ClipboardList },
  { key: "certificates", label: "Certificates", icon: Check },
  { key: "emails", label: "Emails", icon: Send },
  { key: "registration", label: "Registration", icon: UserPlus },
  { key: "proposals", label: "Proposals", icon: FileText },
  { key: "ambassadors", label: "Ambassadors", icon: Award },
  { key: "study-guides", label: "Study guides", icon: FileText },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "enquiries", label: "Enquiries", icon: MessageCircle },
  { key: "comments", label: "Comments", icon: MessageSquare },
  { key: "cpd", label: "CPD entries", icon: FileText },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminPage() {
  useSeo({ title: "Admin", path: "/admin", noindex: true });
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
  }, [loading, user, nav]);

  if (loading || !user || user.role !== "admin") {
    return <div className="min-h-[60vh] grid place-items-center text-sm text-zinc-500">Checking admin access…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="eyebrow mb-1 inline-flex items-center gap-1">
            <ShieldAlert size={13} /> Admin portal
          </div>
          <h1 className="font-display text-3xl sm:text-4xl">CSI control room</h1>
          <p className="text-sm text-zinc-600 mt-1">Signed in as {user.name} · {user.email}</p>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="chip inline-flex items-center gap-1" data-testid="admin-logout">
          <LogOut size={13}/> Sign out
        </button>
      </div>

      <GradingQueue />

      <nav className="flex flex-wrap gap-2 mb-6 border-b border-[var(--border-warm)] pb-2 sticky top-0 bg-[var(--sand)] z-20 pt-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`chip inline-flex items-center gap-1.5 ${tab === t.key ? "bg-[var(--navy)] text-white border-[var(--navy)]" : ""}`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={13}/> {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab />}
      {tab === "users" && <UsersTab currentUser={user} />}
      {tab === "faculty" && <FacultyTab />}
      {tab === "courses" && <CoursesTab />}
      {tab === "live" && <LiveTab />}
      {tab === "enrolments" && <EnrolmentsTab />}
      {tab === "exams" && <ExamsTab />}
      {tab === "certificates" && <CertificatesTab />}
      {tab === "emails" && <EmailLogTab />}
      {tab === "registration" && <CohortRegistrationTab />}
      {tab === "proposals" && <ProposalsTab />}
      {tab === "ambassadors" && <AmbassadorsTab />}
      {tab === "study-guides" && <StudyGuidesTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "enquiries" && <EnquiriesTab currentUser={user} />}
      {tab === "comments" && <CommentsTab />}
      {tab === "cpd" && <CPDTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

/* ========= Overview ========= */
function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <SkeletonCard />;
  const C = ({ label, value, sub }) => (
    <div className="card-warm p-5">
      <div className="eyebrow text-[10px] mb-1">{label}</div>
      <div className="font-display text-3xl text-[var(--navy-deep)]">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
  return (
    <>
    <ResultsOverview />
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-overview">
      <C label="Total users" value={stats.users.total} sub={`${stats.users.students} students · ${stats.users.instructors} instructors · ${stats.users.admins} admin`} />
      <C label="Courses" value={stats.courses.total} sub={`${stats.courses.live} with live cohorts`} />
      <C label="Enrolments" value={stats.enrolments} />
      <C label="Live sessions" value={stats.live_sessions} />
      <C label="Comments" value={stats.comments} />
      <C label="CPD entries" value={stats.cpd_entries} />
      <C label="Approved payments" value={stats.payments.approved} />
      <C label="Revenue (EUR)" value={`€${stats.payments.revenue_eur.toFixed(2)}`} />
    </section>
    </>
  );
}

/* ========= Users ========= */
function UsersTab({ currentUser }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [cmPanel, setCmPanel] = useState(null); // {user, results, loading}
  const { impersonate } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/admin/users", { params: { q: q || undefined, role: role || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${edit.id}`, {
        name: edit.name, email: edit.email, role: edit.role, bio: edit.bio, avatar_url: edit.avatar_url,
      });
      toast.success("User updated");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This removes all their enrolments, CPD and comments${u.role === "instructor" ? " and ALL their courses" : ""}.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReset = async () => {
    if (newPass.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post(`/admin/users/${reset.id}/reset-password`, { new_password: newPass }); toast.success("Password reset"); setReset(null); setNewPass(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const impersonateUser = async (u) => {
    if (!window.confirm(`Log in as ${u.name || u.email}? Your admin session will be preserved - use the banner to return.`)) return;
    try {
      await impersonate(u.id);
      try { localStorage.setItem("csi_impersonating", "1"); } catch {}
      toast.success(`Now signed in as ${u.name || u.email}`);
      nav(u.role === "instructor" ? "/teach" : "/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="space-y-4" data-testid="admin-users">
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="w-full pl-9 pr-4 py-2 rounded-full border border-[var(--border-warm)] bg-white text-sm" data-testid="users-search"/>
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="chip" data-testid="users-role-filter">
          <option value="">All roles</option>
          <option value="student">Student</option>
          <option value="instructor">Instructor</option>
          <option value="admin">Admin</option>
        </select>
        <span className="text-xs text-zinc-500 ml-auto">{rows.length} result{rows.length === 1 ? "" : "s"}</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Created</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-[var(--border-warm)]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">{u.name || "-"}</td>
                <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.role === "admin" ? "bg-[var(--terracotta)] text-white" : u.role === "instructor" ? "bg-[var(--navy)] text-white" : "bg-[var(--muted-surface)] text-[var(--navy-deep)]"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0,10)}</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => setEdit({ ...u })} className="chip inline-flex items-center gap-1 text-xs" data-testid={`edit-user-${u.id}`}><Pencil size={12}/> Edit</button>
                  <button onClick={() => setReset(u)} className="chip inline-flex items-center gap-1 text-xs" data-testid={`reset-user-${u.id}`}><KeyRound size={12}/> Reset</button>
                  {u.role === "student" && <InviteAmbassadorButton u={u} onDone={load}/>}
                  {u.role === "student" && <PortfolioInviteButton u={u} onDone={load}/>}
                  {u.role === "student" && <PortfolioPassButton u={u} onDone={load}/>}
                  <button
                    onClick={async () => {
                      setCmPanel({ user: u, loading: true, results: [] });
                      try {
                        const { data } = await api.get(`/admin/users/${u.id}/classmarker-results`);
                        setCmPanel({ user: u, loading: false, results: data || [] });
                      } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "Could not load scores");
                        setCmPanel(null);
                      }
                    }}
                    className="chip inline-flex items-center gap-1 text-xs"
                    data-testid={`cm-scores-user-${u.id}`}
                  >
                    <GraduationCap size={12}/> Scores
                  </button>
                  {u.id !== currentUser.id && (
                    <>
                      <button onClick={() => impersonateUser(u)} className="chip inline-flex items-center gap-1 text-xs text-[var(--navy)]" data-testid={`impersonate-user-${u.id}`}><Eye size={12}/> Login as</button>
                      <button onClick={() => remove(u)} className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" data-testid={`delete-user-${u.id}`}><Trash2 size={12}/></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={`Edit ${edit.name || edit.email}`} onClose={() => setEdit(null)}>
          <Field label="Name"><input className="field" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-user-name"/></Field>
          <Field label="Email"><input className="field" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-user-email"/></Field>
          <Field label="Role">
            <select className="field" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="edit-user-role">
              <option value="student">Student</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Bio"><textarea className="field" value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })}/></Field>
          <Field label="Avatar URL"><input className="field" value={edit.avatar_url || ""} onChange={(e) => setEdit({ ...edit, avatar_url: e.target.value })}/></Field>
          <ModalActions onCancel={() => setEdit(null)} onSave={saveEdit} saveLabel="Save user"/>
        </Modal>
      )}

      {reset && (
        <Modal title={`Reset password - ${reset.name || reset.email}`} onClose={() => { setReset(null); setNewPass(""); }}>
          <Field label="New password (min 6 chars)">
            <input type="text" className="field" value={newPass} onChange={(e) => setNewPass(e.target.value)} autoFocus data-testid="reset-password-input"/>
          </Field>
          <p className="text-xs text-zinc-500 mb-3">Share this new password securely with the user. They can change it later.</p>
          <ModalActions onCancel={() => { setReset(null); setNewPass(""); }} onSave={doReset} saveLabel="Set password" saveTestId="reset-password-submit"/>
        </Modal>
      )}

      {cmPanel && (
        <Modal title={`ClassMarker scores - ${cmPanel.user.name || cmPanel.user.email}`} onClose={() => setCmPanel(null)} wide>
          {cmPanel.loading ? (
            <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>
          ) : cmPanel.results.length === 0 ? (
            <div className="text-sm text-zinc-500 py-10 text-center" data-testid="cm-scores-empty">No test results received yet for this candidate.</div>
          ) : (
            <div className="overflow-x-auto" data-testid="cm-scores-table">
              <table className="min-w-full text-sm">
                <thead className="text-left">
                  <tr className="border-b border-[var(--border-warm)]">
                    <th className="py-2 pr-4">Test</th>
                    <th className="py-2 pr-4">Score</th>
                    <th className="py-2 pr-4">Pass</th>
                    <th className="py-2 pr-4">Completed</th>
                    <th className="py-2 pr-4">Certificate</th>
                  </tr>
                </thead>
                <tbody>
                  {cmPanel.results.map((r, i) => (
                    <tr key={i} className="border-b border-[var(--border-warm)]">
                      <td className="py-2 pr-4 text-xs">
                        <div>{r.test_title || "-"}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">ID {r.test_id || "-"}</div>
                      </td>
                      <td className="py-2 pr-4 text-xs font-mono">
                        {r.percentage != null ? `${r.percentage}%` : (r.score != null && r.total != null ? `${r.score}/${r.total}` : "-")}
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        {r.passed === true ? (
                          <span className="text-emerald-700 font-semibold">Pass</span>
                        ) : r.passed === false ? (
                          <span className="text-red-700 font-semibold">Fail</span>
                        ) : <span className="text-zinc-400">-</span>}
                      </td>
                      <td className="py-2 pr-4 text-xs">{(r.completed_at || r.received_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 pr-4 text-xs">
                        {r.certificate_url ? (
                          <a href={r.certificate_url} target="_blank" rel="noreferrer" className="text-[var(--terracotta)] underline">View</a>
                        ) : <span className="text-zinc-400">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ModalActions onCancel={() => setCmPanel(null)} onSave={() => setCmPanel(null)} saveLabel="Close"/>
        </Modal>
      )}
      <FieldStyles/>
    </section>
  );
}

/* ========= Faculty ========= */
const emptyFaculty = { slug: "", name: "", title: "", blurb: "", image: "", linkedin: "", type: "guest", qualifications: [], order: 100 };
function FacultyTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/faculty").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...edit, order: Number(edit.order) || 100, qualifications: Array.isArray(edit.qualifications) ? edit.qualifications : String(edit.qualifications || "").split("\n").map(s => s.trim()).filter(Boolean) };
    try {
      if (edit.id) await api.put(`/admin/faculty/${edit.id}`, payload);
      else await api.post("/admin/faculty", payload);
      toast.success(edit.id ? "Faculty updated" : "Faculty created");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try { await api.delete(`/admin/faculty/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="space-y-4" data-testid="admin-faculty">
      <FacultyApplications onApproved={load}/>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-zinc-600">Manage the faculty roster shown on the homepage and Instructors page.</p>
        <button className="btn-terracotta inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold" onClick={() => setEdit({ ...emptyFaculty })} data-testid="add-faculty">
          <Plus size={14}/> Add faculty
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((f) => (
          <article key={f.id} className="card-warm p-4" data-testid={`faculty-card-${f.id}`}>
            <div className="flex gap-3">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-[var(--navy-deep)] shrink-0">
                {f.image && <img src={f.image} alt={f.name} className="w-full h-full object-cover"/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="eyebrow text-[10px]">{f.type}</div>
                <div className="font-display text-base leading-tight truncate">{f.name}</div>
                <div className="text-xs text-zinc-600 truncate">{f.title}</div>
              </div>
            </div>
            <p className="text-xs text-zinc-600 mt-2 line-clamp-3">{f.blurb}</p>
            <div className="mt-3 flex gap-2">
              <button className="chip inline-flex items-center gap-1 text-xs" onClick={() => setEdit({ ...f, qualifications: (f.qualifications || []).join("\n") })} data-testid={`edit-faculty-${f.id}`}><Pencil size={12}/> Edit</button>
              <button className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)] ml-auto" onClick={() => remove(f)} data-testid={`delete-faculty-${f.id}`}><Trash2 size={12}/></button>
            </div>
          </article>
        ))}
        {rows.length === 0 && <div className="col-span-full text-sm text-zinc-500 text-center py-10">No faculty records yet.</div>}
      </div>

      {edit && (
        <Modal title={edit.id ? `Edit ${edit.name}` : "Add faculty"} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Slug (URL-safe)"><input className="field" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} data-testid="faculty-slug"/></Field>
            <Field label="Name"><input className="field" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="faculty-name"/></Field>
            <Field label="Title"><input className="field" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="faculty-title"/></Field>
            <Field label="Type">
              <select className="field" value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>
                <option value="chief">Chief Instructor</option>
                <option value="guest">Guest Lecturer</option>
                <option value="adjunct">Adjunct Faculty</option>
              </select>
            </Field>
            <Field label="Headshot" wide>
              <ImageUpload value={edit.image} onChange={(url) => setEdit({ ...edit, image: url })} kind="faculty"/>
            </Field>
            <Field label="LinkedIn URL (optional)"><input className="field" value={edit.linkedin} onChange={(e) => setEdit({ ...edit, linkedin: e.target.value })}/></Field>
            <Field label="Sort order (lower = earlier)" wide><input type="number" className="field" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })}/></Field>
            <Field label="Blurb" wide><textarea className="field" rows={3} value={edit.blurb} onChange={(e) => setEdit({ ...edit, blurb: e.target.value })} data-testid="faculty-blurb"/></Field>
            <Field label="Qualifications (one per line)" wide><textarea className="field" rows={4} value={edit.qualifications} onChange={(e) => setEdit({ ...edit, qualifications: e.target.value })}/></Field>
          </div>
          <ModalActions onCancel={() => setEdit(null)} onSave={save} saveLabel={edit.id ? "Save faculty" : "Create faculty"} saveTestId="faculty-save"/>
        </Modal>
      )}
      <FieldStyles/>
    </section>
  );
}

/* ========= Courses ========= */
const emptyLesson = () => ({ title: "", content: "", video_url: "", duration_min: 10 });
const emptyCourse = () => ({ title: "", description: "", category: "Entry Level", price: 0, cover_url: "", is_live: false, instructor_id: "", lessons: [emptyLesson()], quiz: [] });

function CoursesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [edit, setEdit] = useState(null); // existing course for edit
  const [creating, setCreating] = useState(null); // new course draft

  const load = async () => {
    setErr(null);
    for (let i = 0; i < 3; i++) {
      try {
        const { data } = await api.get("/admin/courses");
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
        return;
      } catch (e) {
        if (i === 2) { setErr(e); setLoading(false); return; }
        await new Promise(r => setTimeout(r, 700 * (i + 1)));
      }
    }
  };
  const loadInstructors = () => api.get("/admin/users", { params: { role: "instructor" } }).then((r) => setInstructors(r.data)).catch(() => {});
  useEffect(() => { load(); loadInstructors(); }, []);

  const saveEdit = async () => {
    try {
      await api.put(`/courses/${edit.id}`, {
        title: edit.title, description: edit.description, category: edit.category,
        price: Number(edit.price), cover_url: edit.cover_url, is_live: !!edit.is_live,
        instructor_id: edit.instructor_id || undefined,
      });
      // Payment link is managed via dedicated admin endpoint (keeps it strictly admin-only)
      if ((edit.payment_link_url ?? "") !== (edit._original_payment_link ?? "")) {
        await api.patch(`/admin/courses/${edit.id}/payment-link`, { payment_link_url: edit.payment_link_url || "" });
      }
      // ClassMarker per-course mapping - only for exam-only / direct_assessment
      const isExamCourse = ["exam_only", "direct_assessment"].includes(edit.delivery_type || "");
      if (isExamCourse) {
        await api.patch(`/admin/courses/${edit.id}/classmarker`, {
          test_id: edit.classmarker?.test_id || "",
          access_list_id: edit.classmarker?.access_list_id || "",
          test_slug: edit.classmarker?.test_slug || "",
        });
      }
      toast.success("Course saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!creating.title || !creating.description) return toast.error("Title and description are required");
    try {
      const payload = {
        ...creating,
        price: Number(creating.price) || 0,
        instructor_id: creating.instructor_id || undefined,
        lessons: (creating.lessons || []).filter(l => l.title),
        quiz: creating.quiz || [],
      };
      await api.post("/courses", payload);
      toast.success("Course created"); setCreating(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its enrolments, live sessions and comments?`)) return;
    try { await api.delete(`/courses/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="space-y-4" data-testid="admin-courses">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-zinc-600">Edit or delete any course on the platform. Admin can also create a course on behalf of any instructor.</p>
        <button className="btn-terracotta inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold" onClick={() => setCreating(emptyCourse())} data-testid="create-course-btn">
          <Plus size={14}/> Create course
        </button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Instructor</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Live</th>
              <th className="px-4 py-3">Lessons</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">Loading courses…</td></tr>
            ) : err ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center">
                <div className="text-sm text-zinc-700 mb-2">Couldn't reach the catalogue. {err?.response?.status ? `(HTTP ${err.response.status})` : ""}</div>
                <button className="chip" onClick={load}>Retry</button>
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No courses yet. Click "Create course" to add one.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} className="border-t border-[var(--border-warm)]" data-testid={`course-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{c.title}</div>
                  <div className="text-xs text-zinc-500">{c.code || c.id.slice(0,8)}</div>
                </td>
                <td className="px-4 py-3 text-xs">{c.instructor_name || "-"}</td>
                <td className="px-4 py-3 text-xs">{c.category}</td>
                <td className="px-4 py-3 text-xs">€{c.price}</td>
                <td className="px-4 py-3">{c.is_live ? <span className="text-[var(--terracotta)] font-bold text-xs">LIVE</span> : <span className="text-zinc-400 text-xs">-</span>}</td>
                <td className="px-4 py-3 text-xs">{(c.lessons || []).length}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                  <Link to={`/courses/${c.id}`} className="chip text-xs">View</Link>
                  <button className="chip inline-flex items-center gap-1 text-xs" onClick={() => setEdit({ ...c, _original_payment_link: c.payment_link_url || "" })} data-testid={`edit-course-${c.id}`}><Pencil size={12}/> Edit</button>
                  <button className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" onClick={() => remove(c)} data-testid={`delete-course-${c.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
            {false && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No courses</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={`Edit ${edit.title}`} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title" wide><input className="field" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}/></Field>
            <Field label="Description" wide><textarea className="field" rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })}/></Field>
            <Field label="Category">
              <select className="field" value={edit.category || "Entry Level"} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>
                {["Entry Level","Professional","Leadership","Executive","Postgraduate","General"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Price (EUR)"><input type="number" step="0.01" className="field" value={edit.price ?? 0} onChange={(e) => setEdit({ ...edit, price: e.target.value })}/></Field>
            <Field label="Instructor">
              <select className="field" value={edit.instructor_id || ""} onChange={(e) => setEdit({ ...edit, instructor_id: e.target.value })}>
                <option value="">{`Keep current (${edit.instructor_name || "-"})`}</option>
                {instructors.map((u) => <option key={u.id} value={u.id}>{`${u.name} · ${u.email}`}</option>)}
              </select>
            </Field>
            <Field label="Cover image" wide>
              <ImageUpload value={edit.cover_url} onChange={(url) => setEdit({ ...edit, cover_url: url })} kind="course-cover"/>
            </Field>
            <Field label="SumUp payment link" wide>
              <input
                type="url"
                className="field"
                placeholder="https://pay.sumup.com/b2c/XXXXXXXX"
                value={edit.payment_link_url || ""}
                onChange={(e) => setEdit({ ...edit, payment_link_url: e.target.value })}
                data-testid="edit-course-payment-link"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Students will be redirected to this SumUp page when they click "Enrol &amp; Pay". Leave empty to disable online payment for this course.
              </p>
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-2">
            <input type="checkbox" checked={!!edit.is_live} onChange={(e) => setEdit({ ...edit, is_live: e.target.checked })} className="accent-[var(--terracotta)]"/>
            Course offers live cohort sessions
          </label>
          <ModalActions onCancel={() => setEdit(null)} onSave={saveEdit} saveLabel="Save course"/>
        </Modal>
      )}

      {creating && (
        <Modal title="Create a course" onClose={() => setCreating(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title" wide><input className="field" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} data-testid="new-course-title"/></Field>
            <Field label="Short description" wide><textarea className="field" rows={2} value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} data-testid="new-course-desc"/></Field>
            <Field label="Category">
              <select className="field" value={creating.category} onChange={(e) => setCreating({ ...creating, category: e.target.value })}>
                {["Entry Level","Professional","Leadership","Executive","Postgraduate","General"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Price (EUR)"><input type="number" step="0.01" className="field" value={creating.price} onChange={(e) => setCreating({ ...creating, price: e.target.value })} data-testid="new-course-price"/></Field>
            <Field label="Instructor">
              <select className="field" value={creating.instructor_id} onChange={(e) => setCreating({ ...creating, instructor_id: e.target.value })} data-testid="new-course-instructor">
                <option value="">- Assign to me (admin) -</option>
                {instructors.map((u) => <option key={u.id} value={u.id}>{`${u.name} · ${u.email}`}</option>)}
              </select>
            </Field>
            <Field label="Live cohort">
              <label className="inline-flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={creating.is_live} onChange={(e) => setCreating({ ...creating, is_live: e.target.checked })} className="accent-[var(--terracotta)]"/> Enable live sessions
              </label>
            </Field>
            <Field label="Cover image" wide>
              <ImageUpload value={creating.cover_url} onChange={(url) => setCreating({ ...creating, cover_url: url })} kind="course-cover"/>
            </Field>
          </div>

          <div className="mt-4">
            <div className="eyebrow mb-2">Lessons</div>
            {creating.lessons.map((l, i) => (
              <div key={i} className="border border-[var(--border-warm)] rounded-xl p-3 mb-2">
                <div className="flex gap-2 items-center mb-2">
                  <input className="field flex-1" placeholder={`Lesson ${i + 1} title`} value={l.title} onChange={(e) => {
                    const ls = [...creating.lessons]; ls[i] = { ...ls[i], title: e.target.value }; setCreating({ ...creating, lessons: ls });
                  }}/>
                  <input className="field w-24" type="number" placeholder="Min" value={l.duration_min} onChange={(e) => {
                    const ls = [...creating.lessons]; ls[i] = { ...ls[i], duration_min: Number(e.target.value) }; setCreating({ ...creating, lessons: ls });
                  }}/>
                  {creating.lessons.length > 1 && (
                    <button type="button" className="chip" onClick={() => setCreating({ ...creating, lessons: creating.lessons.filter((_, j) => j !== i) })}><Trash2 size={13}/></button>
                  )}
                </div>
                <input className="field mb-2" placeholder="Video URL (YouTube embed or website link)" value={l.video_url} onChange={(e) => {
                  const ls = [...creating.lessons]; ls[i] = { ...ls[i], video_url: e.target.value }; setCreating({ ...creating, lessons: ls });
                }}/>
                <textarea className="field" rows={2} placeholder="Lesson content" value={l.content} onChange={(e) => {
                  const ls = [...creating.lessons]; ls[i] = { ...ls[i], content: e.target.value }; setCreating({ ...creating, lessons: ls });
                }}/>
              </div>
            ))}
            <button type="button" className="chip inline-flex items-center gap-1" onClick={() => setCreating({ ...creating, lessons: [...creating.lessons, emptyLesson()] })}>
              <Plus size={13}/> Add lesson
            </button>
          </div>

          <ModalActions onCancel={() => setCreating(null)} onSave={create} saveLabel="Create course" saveTestId="new-course-save"/>
        </Modal>
      )}
      <FieldStyles/>
    </section>
  );
}

/* ========= Enrolments (admin manual) ========= */
function EnrolmentsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [adding, setAdding] = useState(null); // { user_id, course_id }
  const [cmPanel, setCmPanel] = useState(null); // { user, enrolment, results, loading, expanded }
  const load = () => api.get("/admin/enrollments").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/courses").then((r) => setCourses(r.data));
  }, []);
  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return !t ? rows : rows.filter(r => (r.user_name || "").toLowerCase().includes(t) || (r.user_email || "").toLowerCase().includes(t) || (r.course_title || "").toLowerCase().includes(t));
  }, [q, rows]);
  const submit = async () => {
    if (!adding.user_id || !adding.course_id) return toast.error("Pick both a user and a course");
    try {
      await api.post("/admin/enrollments", { user_id: adding.user_id, course_id: adding.course_id });
      toast.success("Student enrolled");
      setAdding(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Unenrol ${r.user_name} from "${r.course_title}"?`)) return;
    try { await api.delete(`/admin/enrollments/${r.id}`); toast.success("Unenrolled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section className="space-y-4" data-testid="admin-enrolments">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or course…" className="w-full pl-9 pr-4 py-2 rounded-full border border-[var(--border-warm)] bg-white text-sm"/>
        </div>
        <button className="btn-terracotta inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold" onClick={() => setAdding({ user_id: "", course_id: "" })} data-testid="enrol-student-btn">
          <UserPlus size={14}/> Enrol a student
        </button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Enrolled</th>
              <th className="px-4 py-3">Progress</th>
              <th className="px-4 py-3">Exam result</th>
              <th className="px-4 py-3">Receipt</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border-warm)]" data-testid={`enrol-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-xs">{r.user_name}</div>
                  <div className="text-[10px] text-zinc-500">{r.user_email}</div>
                </td>
                <td className="px-4 py-3 text-xs">{r.course_title}</td>
                <td className="px-4 py-3 text-xs">{(r.enrolled_at || "").slice(0,10)}</td>
                <td className="px-4 py-3 text-xs">{(r.progress || []).length} lessons</td>
                <td className="px-4 py-3 text-xs">
                  {r.classmarker_result ? (
                    <button
                      type="button"
                      onClick={async () => {
                        setCmPanel({ user: { id: r.user_id, name: r.user_name, email: r.user_email }, enrolment: r, loading: true, results: [], expanded: null });
                        try {
                          const { data } = await api.get(`/admin/users/${r.user_id}/classmarker-results`);
                          setCmPanel((p) => p ? { ...p, loading: false, results: data || [] } : null);
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail) || "Could not load scores");
                          setCmPanel(null);
                        }
                      }}
                      className="hover:scale-105 transition-transform text-left"
                      data-testid={`enrol-cm-result-${r.id}`}
                      title="Click to see every attempt and the raw ClassMarker payload"
                    >
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.classmarker_result.passed ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                        {r.classmarker_result.percentage != null ? `${r.classmarker_result.percentage}%` : (r.classmarker_result.passed ? "Pass" : "Fail")}
                      </span>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{(r.classmarker_result.completed_at || "").slice(0,10)}</div>
                    </button>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.receipt_pdf_url ? (
                    <a href={r.receipt_pdf_url} target="_blank" rel="noreferrer" className="text-[var(--terracotta)] font-semibold inline-flex items-center gap-1" data-testid={`enrol-receipt-${r.id}`}>
                      <FileText size={12}/> PDF
                    </a>
                  ) : <span className="text-zinc-400">-</span>}
                </td>
                <td className="px-4 py-3 text-xs">{r.provider || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <button className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" onClick={() => remove(r)} data-testid={`unenrol-${r.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No enrolments</td></tr>}
          </tbody>
        </table>
      </div>

      {adding && (
        <Modal title="Enrol a student" onClose={() => setAdding(null)}>
          <Field label="Member">
            <select className="field" value={adding.user_id} onChange={(e) => setAdding({ ...adding, user_id: e.target.value })} data-testid="enrol-user-select">
              <option value="">Select a user…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{`${u.name} · ${u.email} · ${u.role}`}</option>)}
            </select>
          </Field>
          <Field label="Course">
            <select className="field" value={adding.course_id} onChange={(e) => setAdding({ ...adding, course_id: e.target.value })} data-testid="enrol-course-select">
              <option value="">Select a course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{`${c.title} · €${c.price}`}</option>)}
            </select>
          </Field>
          <ModalActions onCancel={() => setAdding(null)} onSave={submit} saveLabel="Enrol" saveTestId="enrol-submit"/>
        </Modal>
      )}

      {cmPanel && (
        <Modal title={`Score history - ${cmPanel.user.name || cmPanel.user.email}${cmPanel.enrolment ? ` · ${cmPanel.enrolment.course_title}` : ""}`} onClose={() => setCmPanel(null)} wide>
          {cmPanel.loading ? (
            <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>
          ) : cmPanel.results.length === 0 ? (
            <div className="text-sm text-zinc-500 py-10 text-center" data-testid="enrol-cm-empty">No test results received yet for this candidate.</div>
          ) : (
            <div className="space-y-3" data-testid="enrol-cm-drill-in">
              <p className="text-[11px] text-zinc-500">
                Showing {cmPanel.results.length} attempt{cmPanel.results.length === 1 ? "" : "s"}. Click a row to expand the raw ClassMarker webhook payload.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left">
                    <tr className="border-b border-[var(--border-warm)]">
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">Test</th>
                      <th className="py-2 pr-4">Score</th>
                      <th className="py-2 pr-4">Pass</th>
                      <th className="py-2 pr-4">Completed</th>
                      <th className="py-2 pr-4">Duration</th>
                      <th className="py-2 pr-4 text-right">Raw payload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cmPanel.results.map((r, i) => (
                      <React.Fragment key={i}>
                        <tr className="border-b border-[var(--border-warm)]">
                          <td className="py-2 pr-4 text-xs text-zinc-500">{cmPanel.results.length - i}</td>
                          <td className="py-2 pr-4 text-xs">
                            <div>{r.test_title || "-"}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">ID {r.test_id || "-"}</div>
                          </td>
                          <td className="py-2 pr-4 text-xs font-mono">
                            {r.percentage != null ? `${r.percentage}%` : (r.score != null && r.total != null ? `${r.score}/${r.total}` : "-")}
                          </td>
                          <td className="py-2 pr-4 text-xs">
                            {r.passed === true ? <span className="text-emerald-700 font-semibold">Pass</span> :
                             r.passed === false ? <span className="text-red-700 font-semibold">Fail</span> :
                             <span className="text-zinc-400">-</span>}
                          </td>
                          <td className="py-2 pr-4 text-xs">{(r.completed_at || r.received_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="py-2 pr-4 text-xs">
                            {r.duration_seconds != null ? `${Math.round(r.duration_seconds / 60)} min` : "-"}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            <button
                              type="button"
                              onClick={() => setCmPanel((p) => ({ ...p, expanded: p.expanded === i ? null : i }))}
                              className="chip text-xs"
                              data-testid={`enrol-cm-raw-toggle-${i}`}
                            >
                              {cmPanel.expanded === i ? "Hide" : "View"}
                            </button>
                          </td>
                        </tr>
                        {cmPanel.expanded === i && (
                          <tr>
                            <td colSpan={7} className="bg-[var(--muted-surface)] px-4 py-3">
                              <pre className="text-[10px] text-zinc-700 overflow-x-auto whitespace-pre-wrap break-words max-h-80" data-testid={`enrol-cm-raw-${i}`}>
{JSON.stringify(r, null, 2)}
                              </pre>
                              {r.certificate_url && (
                                <a href={r.certificate_url} target="_blank" rel="noreferrer" className="text-[11px] text-[var(--terracotta)] underline">Open ClassMarker certificate ↗</a>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <ModalActions onCancel={() => setCmPanel(null)} onSave={() => setCmPanel(null)} saveLabel="Close"/>
        </Modal>
      )}
      <FieldStyles/>
    </section>
  );
}

/* ========= Exams ========= */
function ExamsTab() {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [tab, setTab] = useState("exams"); // exams | submissions
  const [edit, setEdit] = useState(null);
  const [importFor, setImportFor] = useState(null);
  const [importId, setImportId] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const [{ data: e }, { data: c }, { data: a }] = await Promise.all([
        api.get("/admin/exams"),
        api.get("/admin/courses"),
        api.get("/admin/exam-attempts"),
      ]);
      setExams(e || []);
      setCourses(c || []);
      setAttempts(a || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load exams");
    }
  };
  useEffect(() => { load(); }, []);

  const createEmpty = (courseId) => setEdit({
    _new: true,
    course_id: courseId,
    title: "",
    instructions: "",
    time_limit_minutes: 60,
    max_attempts: 2,
    pass_mark_percent: 70,
    shuffle_options: false,
    questions: [],
  });

  const [cloneFor, setCloneFor] = useState(null);
  const [cloneTargetId, setCloneTargetId] = useState("");

  const save = async () => {
    const payload = { ...edit, questions: edit.questions.map((q) => ({ ...q, id: q.id || undefined })) };
    try {
      if (edit._new) {
        delete payload._new;
        await api.post("/admin/exams", payload);
        toast.success("Exam created");
      } else {
        await api.put(`/admin/exams/${edit.id}`, payload);
        toast.success("Exam updated");
      }
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (ex) => {
    if (!window.confirm(`Delete exam "${ex.title}"? All attempts will be removed too.`)) return;
    try { await api.delete(`/admin/exams/${ex.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const doImport = async () => {
    if (!importFor || !importId.trim()) return;
    try {
      const { data } = await api.post(`/admin/exams/${importFor.id}/import-classmarker`, {
        classmarker_test_id: importId.trim(),
        replace_questions: true,
      });
      toast.success(`Imported ${data.imported_count} questions`);
      setImportFor(null); setImportId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl">Native CSI Exam Engine</h2>
          <p className="text-xs text-zinc-500 mt-1">Create, grade and audit exams fully inside CSI. Optionally import question banks from ClassMarker once.</p>
        </div>
        <div className="flex gap-2">
          <button className={`chip text-xs ${tab==="exams"?"bg-[var(--navy)] text-white border-[var(--navy)]":""}`} onClick={() => setTab("exams")} data-testid="exams-subtab-exams">Exams ({exams.length})</button>
          <button className={`chip text-xs ${tab==="submissions"?"bg-[var(--navy)] text-white border-[var(--navy)]":""}`} onClick={() => setTab("submissions")} data-testid="exams-subtab-submissions">Submissions ({attempts.length})</button>
        </div>
      </div>

      {tab === "exams" && (
        <div className="space-y-3">
          {courses.map((c) => {
            const ex = exams.find((e) => e.course_id === c.id);
            return (
              <div key={c.id} className="card-warm p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`exam-course-row-${c.code}`}>
                <div>
                  <div className="eyebrow">{c.code}</div>
                  <div className="font-display text-lg">{c.title}</div>
                  {ex ? (
                    <div className="text-xs text-zinc-500 mt-1">
                      {ex.questions?.length || 0} questions · {ex.time_limit_minutes ? `${ex.time_limit_minutes} min` : "untimed"} · pass {ex.pass_mark_percent ?? "-"}% · {ex.max_attempts} attempts
                      {ex.imported_from_classmarker_id && <span className="ml-2 text-[10px] text-[var(--terracotta)]">· imported from CM {ex.imported_from_classmarker_id}</span>}
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-400 mt-1">No exam yet</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {ex ? (
                    <>
                      <button className="chip text-xs" onClick={() => setEdit({ ...ex })} data-testid={`edit-exam-${c.code}`}><Pencil size={12}/> Edit</button>
                      <button className="chip text-xs" onClick={() => { setCloneFor(ex); setCloneTargetId(""); }} data-testid={`clone-exam-${c.code}`}><Upload size={12}/> Clone</button>
                      <button className="chip text-xs" onClick={() => { setImportFor(ex); setImportId(""); }} data-testid={`import-cm-${c.code}`}><Download size={12}/> Import CM</button>
                      <button className="chip text-xs text-[var(--terracotta)]" onClick={() => remove(ex)} data-testid={`delete-exam-${c.code}`}><Trash2 size={12}/></button>
                    </>
                  ) : (
                    <button className="btn-ink text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1" onClick={() => createEmpty(c.id)} data-testid={`create-exam-${c.code}`}><Plus size={12}/> Create exam</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "submissions" && (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} className="chip text-xs" data-testid="submissions-course-filter">
              <option value="">All courses</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{`${c.code} - ${c.title}`}</option>)}
            </select>
            <button
              onClick={async () => {
                try {
                  const res = await api.get(`/admin/export/exam-attempts${filterCourse ? `?course_id=${filterCourse}` : ""}`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `csi-exam-attempts-${filterCourse ? filterCourse.slice(0, 8) : "all"}.csv`;
                  document.body.appendChild(a); a.click();
                  window.URL.revokeObjectURL(url); a.remove();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
                }
              }}
              className="chip text-xs inline-flex items-center gap-1"
              data-testid="export-attempts-csv"
            >
              <Download size={12}/> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
          <table className="min-w-full text-sm bg-white">
            <thead className="bg-[var(--muted-surface)] text-left">
              <tr>
                <th className="px-4 py-3">Candidate</th>
                <th className="px-4 py-3">Exam · Course</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Auto</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3 text-right">Grade</th>
              </tr>
            </thead>
            <tbody>
              {attempts.filter((a) => !filterCourse || a.course_id === filterCourse).map((a) => (
                <tr key={a.id} className="border-t border-[var(--border-warm)]" data-testid={`attempt-row-${a.id}`}>
                  <td className="px-4 py-3 text-xs"><div className="font-semibold">{a.user_name}</div><div className="text-[10px] text-zinc-500">{a.user_email}</div></td>
                  <td className="px-4 py-3 text-xs">{a.exam_title}<div className="text-[10px] text-zinc-500">{a.course_code}</div></td>
                  <td className="px-4 py-3 text-xs">{(a.submitted_at || a.started_at || "").slice(0,16).replace("T"," ")}</td>
                  <td className="px-4 py-3 text-xs font-mono">{a.auto_score}/{a.auto_total}</td>
                  <td className="px-4 py-3 text-xs">
                    <StatusChip status={a.status}/>
                    {a.resit_waived && <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`resit-waived-${a.id}`}>resit granted</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(a.integrity_events || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`attempt-flags-${a.id}`}>
                        <AlertCircle size={12}/> {a.integrity_events.length}
                      </span>
                    ) : <span className="text-zinc-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {a.status === "failed" && !a.resit_waived && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Grant ${a.user_name} a fresh attempt beyond the standard cap?`)) return;
                          try {
                            await api.post(`/admin/exam-attempts/${a.id}/grant-resit`);
                            toast.success("Resit granted - the candidate can start a new attempt.");
                            load();
                          } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                        }}
                        className="chip text-xs mr-1 inline-flex items-center gap-1 text-emerald-700"
                        data-testid={`grant-resit-${a.id}`}
                      ><HelpCircle size={12}/> Grant resit</button>
                    )}
                    <button onClick={() => nav(`/admin/exam-attempts/${a.id}`)} className="chip inline-flex items-center gap-1 text-xs" data-testid={`grade-attempt-${a.id}`}>
                      <GraduationCap size={12}/> Review
                    </button>
                  </td>
                </tr>
              ))}
              {attempts.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No submissions yet</td></tr>}
            </tbody>
          </table>
        </div>
        </>
      )}

      {edit && (
        <Modal title={edit._new ? "Create exam" : `Edit - ${edit.title}`} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title"><input className="field" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="edit-exam-title"/></Field>
            <Field label="Time limit (minutes, blank = untimed)">
              <input type="number" className="field" value={edit.time_limit_minutes ?? ""} onChange={(e) => setEdit({ ...edit, time_limit_minutes: e.target.value === "" ? null : Number(e.target.value) })} data-testid="edit-exam-time"/>
            </Field>
            <Field label="Max attempts">
              <input type="number" min={1} className="field" value={edit.max_attempts} onChange={(e) => setEdit({ ...edit, max_attempts: Math.max(1, Number(e.target.value||1)) })} data-testid="edit-exam-attempts"/>
            </Field>
            <Field label="Pass mark %">
              <input type="number" className="field bg-zinc-100" value={70} readOnly title="All CSI examinations use a 70% pass mark" data-testid="edit-exam-pass"/>
            </Field>
            <Field label="Options">
              <label className="inline-flex items-center gap-2 text-sm pt-2 cursor-pointer">
                <input type="checkbox" checked={!!edit.shuffle_options} onChange={(e) => setEdit({ ...edit, shuffle_options: e.target.checked })} data-testid="edit-exam-shuffle"/>
                <span>Shuffle MCQ options for each candidate</span>
              </label>
            </Field>
          </div>
          <Field label="Instructions (shown to candidate)">
            <textarea className="field" rows={3} value={edit.instructions} onChange={(e) => setEdit({ ...edit, instructions: e.target.value })} data-testid="edit-exam-instr"/>
          </Field>

          <div className="eyebrow mt-3 mb-2 flex flex-wrap items-center gap-2">
            Questions ({edit.questions.length})
            {edit.questions.some((q) => q.ai_generated) && (
              <span className="normal-case tracking-normal rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]" data-testid="exam-review-progress">
                {edit.questions.filter((q) => q.ai_generated && q.reviewed).length}/{edit.questions.filter((q) => q.ai_generated).length} AI-drafted answers reviewed
              </span>
            )}
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {edit.questions.map((q, i) => (
              <div key={q.id || i} className="p-3 border border-[var(--border-warm)] rounded-xl" data-testid={`edit-question-${i}`}>
                <div className="flex items-center gap-2">
                  <select value={q.type} onChange={(e) => {
                    const updated = [...edit.questions];
                    updated[i] = { ...q, type: e.target.value, options: e.target.value === "tf" ? ["True","False"] : (q.options?.length ? q.options : []), correct_index: null };
                    setEdit({ ...edit, questions: updated });
                  }} className="field w-28" data-testid={`q-type-${i}`}>
                    <option value="mcq">MCQ</option>
                    <option value="tf">True/False</option>
                    <option value="essay">Essay</option>
                  </select>
                  <input className="field flex-1" placeholder={`Question ${i+1} prompt`} value={q.prompt} onChange={(e) => {
                    const u = [...edit.questions]; u[i] = { ...q, prompt: e.target.value }; setEdit({ ...edit, questions: u });
                  }} data-testid={`q-prompt-${i}`}/>
                  <input type="number" className="field w-20" placeholder="pts" value={q.points} onChange={(e) => {
                    const u = [...edit.questions]; u[i] = { ...q, points: Math.max(0, Number(e.target.value||0)) }; setEdit({ ...edit, questions: u });
                  }} data-testid={`q-points-${i}`}/>
                  {q.type !== "essay" && q.id && edit.id && (
                    <label className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold cursor-pointer ${q.reviewed ? "bg-emerald-100 text-emerald-800" : q.ai_generated ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600"}`} title={q.reviewed ? `Reviewed by ${q.reviewed_by || "admin"}` : q.ai_generated ? "AI-drafted answer - please review" : "Mark answer as reviewed"} data-testid={`q-reviewed-${i}`}>
                      <input type="checkbox" checked={!!q.reviewed} onChange={async (e) => {
                        try {
                          const { data } = await api.post(`/admin/exams/${edit.id}/questions/${q.id}/review`, { reviewed: e.target.checked });
                          const u = [...edit.questions]; u[i] = { ...q, reviewed: data.reviewed, reviewed_by: data.reviewed_by, reviewed_at: data.reviewed_at }; setEdit({ ...edit, questions: u });
                        } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Failed"); }
                      }} data-testid={`q-reviewed-checkbox-${i}`}/>
                      {q.reviewed ? "Reviewed" : q.ai_generated ? "AI draft" : "Review"}
                    </label>
                  )}
                  <button className="chip text-xs text-[var(--terracotta)]" onClick={() => setEdit({ ...edit, questions: edit.questions.filter((_, j) => j !== i) })} data-testid={`q-delete-${i}`}><Trash2 size={12}/></button>
                </div>
                {(q.type === "mcq" || q.type === "tf") && (
                  <div className="mt-2 space-y-1">
                    {(q.options || []).map((opt, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <input type="radio" name={`correct-${i}`} checked={q.correct_index === j} onChange={() => {
                          const u = [...edit.questions]; u[i] = { ...q, correct_index: j }; setEdit({ ...edit, questions: u });
                        }} data-testid={`q-correct-${i}-${j}`}/>
                        <input className="field flex-1 text-xs" value={opt} onChange={(e) => {
                          const u = [...edit.questions]; const opts = [...(q.options||[])]; opts[j] = e.target.value; u[i] = { ...q, options: opts }; setEdit({ ...edit, questions: u });
                        }} data-testid={`q-option-${i}-${j}`}/>
                        {q.type === "mcq" && (
                          <button className="chip text-[10px] text-[var(--terracotta)]" onClick={() => {
                            const u = [...edit.questions]; const opts = (q.options||[]).filter((_, k) => k !== j);
                            const ci = q.correct_index === j ? null : q.correct_index > j ? q.correct_index - 1 : q.correct_index;
                            u[i] = { ...q, options: opts, correct_index: ci }; setEdit({ ...edit, questions: u });
                          }}><X size={10}/></button>
                        )}
                      </div>
                    ))}
                    {q.type === "mcq" && (
                      <button className="chip text-[10px]" onClick={() => {
                        const u = [...edit.questions]; u[i] = { ...q, options: [...(q.options||[]), ""] }; setEdit({ ...edit, questions: u });
                      }} data-testid={`q-add-option-${i}`}>+ Add option</button>
                    )}
                  </div>
                )}
                {q.type === "essay" && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                      <label className="eyebrow">Grading rubric (examiner-only)</label>
                      <select
                        className="chip text-[11px] py-1"
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const u = [...edit.questions]; u[i] = { ...q, rubric: e.target.value }; setEdit({ ...edit, questions: u });
                          e.target.value = "";
                        }}
                        defaultValue=""
                        data-testid={`q-rubric-template-${i}`}
                      >
                        <option value="">Load CSI template…</option>
                        <option value={`CSI STANDARD RUBRIC (0 / 5 / 10 / 15 pts)\n\n0 pts - Not attempted, off-topic or clearly plagiarised.\n5 pts - Partial attempt. Addresses one or two bullet points. Little structure, limited CSI-standard terminology, no evidence base.\n10 pts - Addresses most bullet points. Reasonable structure. Correctly cites CSI standards (RMA.001, IRBC.001 etc.) but analysis stays descriptive.\n15 pts - Full coverage of every required bullet. Clear executive-level structure. Applies CSI frameworks with evidence, examples and justified recommendations.`}>CSI 15-pt scenario rubric</option>
                        <option value={`CSI 20-PT GOVERNANCE RUBRIC (0 / 10 / 15 / 20 pts)\n\n0 pts - Not attempted or off-topic.\n10 pts - Covers the governance scaffold (CCSO, charter, committee) but omits accountability mechanisms.\n15 pts - Governance + accountability + reporting mechanisms addressed. Minimum word count met. Some evidence.\n20 pts - Executive-ready synthesis: governance, accountability, reporting, maturity, regulatory alignment, with examples. All required bullet points addressed with CSI framework references.`}>CSI 20-pt governance rubric</option>
                        <option value={`CSI 25-PT STRATEGIC CASE-STUDY RUBRIC (0 / 10 / 18 / 25 pts)\n\n0 pts - Not attempted or no coherent strategy.\n10 pts - Addresses current state and some transformation elements. Weak accountability framework or metrics.\n18 pts - Full three-phase roadmap. Addresses governance, stakeholder engagement, metrics. Minimum word count met.\n25 pts - Board-ready, phased transformation with explicit governance, accountability, reporting, maturity assessment and measurable success criteria. Evidence-based and uses CSI frameworks throughout.`}>CSI 25-pt case-study rubric</option>
                      </select>
                    </div>
                    <textarea
                      rows={4}
                      className="field text-xs"
                      placeholder="Load a CSI template above or write a custom rubric."
                      value={q.rubric || ""}
                      onChange={(e) => {
                        const u = [...edit.questions]; u[i] = { ...q, rubric: e.target.value }; setEdit({ ...edit, questions: u });
                      }}
                      data-testid={`q-rubric-${i}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <button className="chip text-xs mt-3" onClick={() => setEdit({ ...edit, questions: [...edit.questions, { id: "", type: "mcq", prompt: "", options: ["", ""], correct_index: 0, points: 1 }] })} data-testid="add-question-btn">
            <Plus size={12}/> Add question
          </button>
          <ModalActions onCancel={() => setEdit(null)} onSave={save} saveLabel={edit._new ? "Create exam" : "Save changes"} saveTestId="save-exam-btn"/>
        </Modal>
      )}

      {importFor && (
        <Modal title={`Import questions from ClassMarker`} onClose={() => setImportFor(null)}>
          <p className="text-xs text-zinc-600 mb-3">
            Paste the ClassMarker test ID. The questions and correct answers will be imported into CSI's local exam engine. ClassMarker credentials must be set in Settings.
          </p>
          <Field label="ClassMarker test ID">
            <input className="field font-mono" value={importId} onChange={(e) => setImportId(e.target.value)} autoFocus data-testid="import-cm-id-input"/>
          </Field>
          <p className="text-[11px] text-amber-700">Existing questions on <b>{importFor.title}</b> will be replaced.</p>
          <ModalActions onCancel={() => setImportFor(null)} onSave={doImport} saveLabel="Import" saveTestId="import-cm-submit"/>
        </Modal>
      )}

      {cloneFor && (
        <Modal title={`Clone exam - ${cloneFor.title}`} onClose={() => { setCloneFor(null); setCloneTargetId(""); }}>
          <p className="text-xs text-zinc-600 mb-3">
            Pick the course where the clone should live. All {cloneFor.questions?.length || 0} questions, instructions, time limit, pass mark and shuffle setting will be duplicated. Edits made afterwards do not sync back to the source.
          </p>
          <Field label="Target course">
            <select className="field" value={cloneTargetId} onChange={(e) => setCloneTargetId(e.target.value)} autoFocus data-testid="clone-target-select">
              <option value="">- Select course -</option>
              {courses.filter((c) => c.id !== cloneFor.course_id).map((c) => (
                <option key={c.id} value={c.id}>{`${c.code} - ${c.title}`}</option>
              ))}
            </select>
          </Field>
          <ModalActions
            onCancel={() => { setCloneFor(null); setCloneTargetId(""); }}
            onSave={async () => {
              if (!cloneTargetId) { toast.error("Pick a target course"); return; }
              try {
                const { data } = await api.post(`/admin/exams/${cloneFor.id}/clone`, { target_course_id: cloneTargetId, title_suffix: " (copy)" });
                toast.success(`Cloned - "${data.title}"`);
                setCloneFor(null); setCloneTargetId("");
                load();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Clone failed"); }
            }}
            saveLabel="Clone exam"
            saveTestId="clone-exam-submit"
          />
        </Modal>
      )}

      <FieldStyles/>
    </section>
  );
}

function StatusChip({ status }) {
  const map = {
    in_progress: ["bg-amber-100 text-amber-800", "In progress"],
    submitted: ["bg-blue-100 text-blue-800", "Pending"],
    graded: ["bg-zinc-200 text-zinc-700", "Graded"],
    passed: ["bg-emerald-100 text-emerald-800", "Pass"],
    failed: ["bg-red-100 text-red-800", "Fail"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-700", status];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{label}</span>;
}

/* ========= Enquiries ========= */
function EnquiriesTab({ currentUser }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/enquiries").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((e) =>
      (e.subject || "").toLowerCase().includes(needle) ||
      (e.user_name || "").toLowerCase().includes(needle) ||
      (e.user_email || "").toLowerCase().includes(needle) ||
      (e.course_title || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const openCount = rows.filter((r) => r.status === "open").length;
  const totalMessages = rows.reduce((a, r) => a + (r.messages?.length || 0), 0);

  const reply = async (e) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.post(`/enquiries/${e.id}/reply`, { text: replyText.trim() });
      toast.success("Reply sent to candidate");
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reply failed");
    } finally { setSending(false); }
  };

  const closeThread = async (e) => {
    try {
      await api.post(`/enquiries/${e.id}/reply`, { text: "- Thread closed by CSI admin -" });
      toast.success("Thread closed");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl">Candidate enquiries</h2>
          <p className="text-xs text-zinc-500 mt-1">{rows.length} thread{rows.length === 1 ? "" : "s"} · {openCount} open · {totalMessages} total messages</p>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} className="pl-7 pr-3 py-1.5 rounded-full border border-[var(--border-warm)] text-sm" placeholder="Search subject / candidate / course" data-testid="enquiries-search"/>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 card-warm p-10 text-center" data-testid="enquiries-empty">No enquiries{q ? " matching your filter" : " yet"}.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const last = e.messages?.[e.messages.length - 1];
            const unanswered = last && last.role === "student";
            return (
              <div key={e.id} className="card-warm p-5" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="eyebrow inline-flex items-center gap-2">
                      {e.course_title}
                      {unanswered && <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" data-testid={`admin-enquiry-pending-${e.id}`}>awaiting reply</span>}
                    </div>
                    <h3 className="font-display text-lg">{e.subject}</h3>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      From <b>{e.user_name}</b> &lt;{e.user_email}&gt; · Opened {new Date(e.created_at).toLocaleString()} · {e.messages?.length || 0} message{(e.messages?.length || 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${e.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{e.status}</span>
                </div>
                <ul className="mt-4 space-y-2">
                  {(e.messages || []).map((m) => (
                    <li key={m.id} className={`rounded-xl p-3 ${m.role === "student" ? "bg-[var(--muted-surface)]" : m.role === "admin" ? "bg-[var(--navy)]/5 border-l-2 border-[var(--terracotta)]" : "bg-[var(--navy)]/5 border-l-2 border-[var(--gold-deep)]"}`}>
                      <div className="text-[11px] text-zinc-500 mb-1"><b>{m.by_name}</b> · {m.role} · {new Date(m.at).toLocaleString()}</div>
                      <div className="text-sm text-zinc-800 whitespace-pre-wrap">{m.text}</div>
                    </li>
                  ))}
                </ul>
                {replyFor === e.id ? (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <textarea
                      value={replyText}
                      onChange={(ev) => setReplyText(ev.target.value)}
                      placeholder="Reply to the candidate…"
                      className="flex-1 min-w-[240px] p-2 border border-[var(--border-warm)] rounded-lg text-sm"
                      rows={3}
                      autoFocus
                      data-testid={`admin-reply-input-${e.id}`}
                    />
                    <div className="flex flex-col gap-2">
                      <button disabled={sending} onClick={() => reply(e)} className="btn-terracotta inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm" data-testid={`admin-reply-send-${e.id}`}>
                        <Send size={13}/> {sending ? "Sending…" : "Send"}
                      </button>
                      <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="chip text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button onClick={() => { setReplyFor(e.id); setReplyText(""); }} className="chip text-xs inline-flex items-center gap-1" data-testid={`admin-reply-${e.id}`}><Send size={12}/> Reply</button>
                    <a href={`mailto:${e.user_email}?subject=Re: ${encodeURIComponent(e.subject)}`} className="chip text-xs inline-flex items-center gap-1"><Mail size={12}/> Email directly</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ========= Payments ========= */
function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [staleOnly, setStaleOnly] = useState(false);
  const [inspect, setInspect] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null); // {subject, html, loading}
  const load = () => api.get("/admin/payment-intents", { params: { status: status || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const isStale = (p) => {
    if (p.status !== "awaiting_payment") return false;
    const created = new Date(p.created_at || 0).getTime();
    return (Date.now() - created) > 24 * 3600 * 1000;
  };
  const displayRows = staleOnly ? rows.filter(isStale) : rows;
  const staleCount = rows.filter(isStale).length;
  const nudgeEmail = (p) => {
    const to = p.user_email || "";
    const name = (p.user_name || "there").split(" ")[0];
    const subject = `CSI payment - ${p.course_title || "your programme"} is waiting for confirmation`;
    const body = `Hi ${name},

A quick note from the Converged Security Institute - you started an enrolment for ${p.course_title}${p.tier_label ? ` (${p.tier_label})` : ""} on ${(p.created_at || "").slice(0,10)} but we haven't yet received your SumUp confirmation.

If you've paid, please reply to this email with the SumUp transaction reference or a photo of your receipt - we'll activate your enrolment within one business day.

If you'd rather complete the payment now, you can reopen the SumUp page here:
${p.payment_link_url || "https://convergedsecurity.es"}

Any questions, just reply.

Best regards,
Prof. Dr. Vladimir Bunic
Chief Instructor & Managing Director
Converged Security Institute`;
    const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };
  const approve = async (p) => {
    if (!window.confirm(`Approve payment from ${p.user_email || p.user_id} and enrol them in the course?`)) return;
    try { await api.post(`/admin/payment-intents/${p.id}/approve`); toast.success("Approved and enrolled"); setInspect(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete this payment record (${p.user_email || p.user_id}, ${p.course_title || ""})? Any enrolment already granted stays in place. This cannot be undone.`)) return;
    try { await api.delete(`/admin/payment-intents/${p.id}`); toast.success("Payment deleted"); setInspect(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (p) => {
    if (!window.confirm(`Reject payment from ${p.user_email || p.user_id}?`)) return;
    try { await api.post(`/admin/payment-intents/${p.id}/reject`); toast.success("Rejected"); setInspect(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const STATUS_LABEL = {
    awaiting_payment: "Awaiting payment",
    pending_review: "Pending review",
    approved: "Approved",
    rejected: "Rejected",
    expired: "Expired",
  };
  const badge = (s) => {
    const colors = {
      awaiting_payment: "bg-zinc-100 text-zinc-700",
      pending_review: "bg-amber-100 text-amber-800",
      approved: "bg-emerald-100 text-emerald-800",
      rejected: "bg-red-100 text-red-800",
      expired: "bg-zinc-200 text-zinc-500",
    };
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${colors[s] || "bg-zinc-100 text-zinc-700"}`}>{STATUS_LABEL[s] || s}</span>;
  };
  const canAct = (p) => p.status === "awaiting_payment" || p.status === "pending_review";
  return (
    <section className="space-y-4" data-testid="admin-payments">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="chip" data-testid="payments-status-filter">
          <option value="">All statuses</option>
          <option value="awaiting_payment">Awaiting payment</option>
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>
        <button
          type="button"
          onClick={() => setStaleOnly((v) => !v)}
          className={`chip inline-flex items-center gap-1 text-xs font-semibold ${staleOnly ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]" : ""}`}
          title="Awaiting payment for more than 24 hours - nudge them to confirm"
          data-testid="payments-stale-filter"
        >
          <AlertCircle size={12}/> Stale &gt;24h
          {staleCount > 0 && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${staleOnly ? "bg-white text-[var(--terracotta)]" : "bg-[var(--terracotta)] text-white"}`}>{staleCount}</span>}
        </button>
        <span className="text-xs text-zinc-500 ml-auto">{displayRows.length} payment intents</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Proof</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((p) => (
              <tr key={p.id} className="border-t border-[var(--border-warm)]" data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 text-xs">
                  <div className="font-semibold">{p.user_name || p.user_email || "-"}</div>
                  <div className="text-[10px] text-zinc-500">{p.user_email || p.user_id?.slice(0,8)}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{p.course_title || p.course_id?.slice(0,8)}</div>
                  {p.tier_label && <div className="text-[10px] text-zinc-500">{p.tier_label}</div>}
                </td>
                <td className="px-4 py-3 text-xs font-semibold">€{p.amount || 0}</td>
                <td className="px-4 py-3">
                  {badge(p.status)}
                  {isStale(p) && <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold text-[var(--terracotta)] uppercase tracking-wider"><AlertCircle size={10}/> Stale</div>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {p.proof_url ? (
                    <a href={p.proof_url} target="_blank" rel="noreferrer" title={p.sumup_reference ? `Ref: ${p.sumup_reference}` : "Open full receipt"} className="inline-flex items-center gap-2">
                      <img src={p.proof_url} alt="Receipt" className="w-12 h-12 rounded-lg object-cover border border-[var(--border-warm)] shadow-sm hover:scale-110 transition-transform"/>
                      {p.sumup_reference && <span className="font-mono text-[10px] text-zinc-600 truncate max-w-[6rem]">{p.sumup_reference}</span>}
                    </a>
                  ) : p.sumup_reference ? (
                    <span className="font-mono text-[11px]">{p.sumup_reference}</span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{(p.created_at || "").slice(0,16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                  <button className="chip inline-flex items-center gap-1 text-xs" onClick={() => setInspect(p)} data-testid={`inspect-payment-${p.id}`}><Eye size={12}/> Inspect</button>
                  <button className="chip inline-flex items-center gap-1 text-xs text-red-700" onClick={() => remove(p)} title="Delete payment record" data-testid={`delete-payment-${p.id}`}><Trash2 size={12}/> Delete</button>
                  {canAct(p) && p.user_email && (
                    <button className="chip inline-flex items-center gap-1 text-xs text-[var(--navy)]" onClick={() => nudgeEmail(p)} title="Send a confirmation reminder email" data-testid={`nudge-payment-${p.id}`}><Mail size={12}/> Nudge</button>
                  )}
                  {canAct(p) && (
                    <>
                      <button className="chip inline-flex items-center gap-1 text-xs text-emerald-700" onClick={() => approve(p)} data-testid={`approve-payment-${p.id}`}><Check size={12}/> Approve</button>
                      <button className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" onClick={() => reject(p)} data-testid={`reject-payment-${p.id}`}><Ban size={12}/> Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {displayRows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">{staleOnly ? "No stale intents - nothing to nudge." : "No payment intents"}</td></tr>}
          </tbody>
        </table>
      </div>

      {inspect && (
        <Modal title="Payment details" onClose={() => setInspect(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <InspectRow label="Status">{badge(inspect.status)}</InspectRow>
            <InspectRow label="Amount">€{inspect.amount || 0} {inspect.currency?.toUpperCase() || "EUR"}</InspectRow>
            <InspectRow label="Member">{inspect.user_name || "-"}<div className="text-[11px] text-zinc-500">{inspect.user_email}</div></InspectRow>
            <InspectRow label="Course">{inspect.course_title || "-"}{inspect.tier_label && <div className="text-[11px] text-zinc-500">{inspect.tier_label}</div>}</InspectRow>
            <InspectRow label="Intent ID"><span className="font-mono text-[11px] break-all">{inspect.id}</span></InspectRow>
            <InspectRow label="Provider">{inspect.provider || "sumup"}</InspectRow>
            <InspectRow label="SumUp reference">{inspect.sumup_reference ? <span className="font-mono">{inspect.sumup_reference}</span> : <span className="text-zinc-400">Not provided</span>}</InspectRow>
            <InspectRow label="SumUp link">
              {inspect.payment_link_url ? (
                <a href={inspect.payment_link_url} target="_blank" rel="noreferrer" className="text-[var(--terracotta)] underline break-all text-xs">{inspect.payment_link_url}</a>
              ) : <span className="text-zinc-400">-</span>}
            </InspectRow>
            <InspectRow label="Created">{(inspect.created_at || "").replace("T", " ").slice(0,19)}</InspectRow>
            <InspectRow label="Updated">{(inspect.updated_at || "").replace("T", " ").slice(0,19)}</InspectRow>
            {inspect.paid_at && <InspectRow label="Member marked paid">{inspect.paid_at.replace("T", " ").slice(0,19)}</InspectRow>}
            {inspect.approved_at && <InspectRow label={inspect.status === "rejected" ? "Rejected at" : "Approved at"}>{inspect.approved_at.replace("T", " ").slice(0,19)}</InspectRow>}
          </div>
          {inspect.proof_url && (
            <div className="mt-4">
              <div className="eyebrow mb-2">Proof of payment</div>
              <a href={inspect.proof_url} target="_blank" rel="noreferrer" className="block">
                <img src={inspect.proof_url} alt="Payment proof" className="max-h-80 rounded-xl border border-[var(--border-warm)]"/>
              </a>
              <a href={inspect.proof_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--terracotta)] underline mt-2 inline-block">Open full-size</a>
            </div>
          )}
          <div className="flex gap-2 justify-end pt-4 border-t border-[var(--border-warm)] mt-4">
            <button
              className="chip inline-flex items-center gap-1 text-sm"
              onClick={async () => {
                setEmailPreview({ loading: true });
                try {
                  const { data } = await api.post(`/admin/payment-intents/${inspect.id}/preview-email`);
                  setEmailPreview({ loading: false, subject: data.subject, html: data.html, to: inspect.user_email });
                } catch (e) {
                  setEmailPreview(null);
                  toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
                }
              }}
              data-testid="inspect-preview-email-btn"
            >
              <Mail size={14}/> Preview email
            </button>
            {canAct(inspect) && (
              <>
                <button className="chip inline-flex items-center gap-1 text-sm text-[var(--terracotta)]" onClick={() => reject(inspect)} data-testid="inspect-reject-btn"><Ban size={14}/> Reject</button>
                <button className="btn-terracotta inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold" onClick={() => approve(inspect)} data-testid="inspect-approve-btn"><Check size={14}/> Approve & enrol</button>
              </>
            )}
            <button className="chip" onClick={() => setInspect(null)}>Close</button>
          </div>
        </Modal>
      )}

      {emailPreview && (
        <Modal title="Approval email preview" onClose={() => setEmailPreview(null)} wide>
          {emailPreview.loading ? (
            <div className="text-sm text-zinc-500 py-10 text-center">Rendering preview…</div>
          ) : (
            <div className="space-y-3" data-testid="email-preview-modal">
              <div className="text-xs text-zinc-600">
                <strong>To:</strong> {emailPreview.to} &nbsp;·&nbsp; <strong>Subject:</strong> {emailPreview.subject}
              </div>
              <div className="rounded-xl border border-[var(--border-warm)] overflow-hidden bg-white">
                <iframe
                  title="email-preview"
                  srcDoc={emailPreview.html}
                  sandbox=""
                  style={{ width: "100%", height: "520px", border: 0 }}
                  data-testid="email-preview-iframe"
                />
              </div>
              <p className="text-[11px] text-zinc-500">This is a non-destructive preview. ClassMarker is not called and no PDF receipt is generated - those happen only when you click <strong>Approve &amp; enrol</strong>.</p>
            </div>
          )}
          <ModalActions onCancel={() => setEmailPreview(null)} onSave={() => setEmailPreview(null)} saveLabel="Close preview"/>
        </Modal>
      )}
    </section>
  );
}

function InspectRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/* ========= Live sessions ========= */
function LiveTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    setErr(null);
    for (let i = 0; i < 3; i++) {
      try {
        const { data } = await api.get("/admin/live-sessions");
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
        return;
      } catch (e) {
        if (i === 2) { setErr(e); setLoading(false); return; }
        await new Promise(r => setTimeout(r, 700 * (i + 1)));
      }
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/live-sessions/${edit.id}`, {
        title: edit.title, scheduled_at: edit.scheduled_at,
        duration_min: Number(edit.duration_min), meeting_url: edit.meeting_url,
        zoom_url: edit.zoom_url || "",
        zoom_passcode: edit.zoom_passcode || "",
        join_instructions: edit.join_instructions || "",
      });
      toast.success("Session updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete session "${s.title}"?`)) return;
    try { await api.delete(`/admin/live-sessions/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="space-y-4" data-testid="admin-live">
      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3">Session</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Scheduled (UTC)</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">Loading live sessions…</td></tr>
            ) : err ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center">
                <div className="text-sm text-zinc-700 mb-2">Couldn't reach the schedule.</div>
                <button className="chip" onClick={load}>Retry</button>
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No live sessions scheduled yet.</td></tr>
            ) : rows.map((s) => (
              <tr key={s.id} className="border-t border-[var(--border-warm)]" data-testid={`live-row-${s.id}`}>
                <td className="px-4 py-3 font-semibold">{s.title}</td>
                <td className="px-4 py-3 text-xs">{s.course_title || s.course_id?.slice(0,8)}</td>
                <td className="px-4 py-3 text-xs">{(s.scheduled_at || "").slice(0,16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-xs">{s.duration_min || 0} min</td>
                <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                  <button className="chip inline-flex items-center gap-1 text-xs" onClick={() => setEdit({ ...s, scheduled_at: (s.scheduled_at || "").slice(0,16) })} data-testid={`edit-live-${s.id}`}><Pencil size={12}/> Edit</button>
                  <button className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" onClick={() => remove(s)} data-testid={`delete-live-${s.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
            {false && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No live sessions</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={`Edit "${edit.title}"`} onClose={() => setEdit(null)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title" wide><input className="field" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}/></Field>
            <Field label="Scheduled at (UTC)"><input type="datetime-local" className="field" value={edit.scheduled_at} onChange={(e) => setEdit({ ...edit, scheduled_at: e.target.value })} data-testid="live-scheduled-at"/></Field>
            <Field label="Duration (min)"><input type="number" className="field" value={edit.duration_min} onChange={(e) => setEdit({ ...edit, duration_min: e.target.value })} data-testid="live-duration"/></Field>
            <Field label="Zoom meeting URL" wide>
              <input className="field" placeholder="https://zoom.us/j/…" value={edit.zoom_url || ""} onChange={(e) => setEdit({ ...edit, zoom_url: e.target.value })} data-testid="live-zoom-url"/>
            </Field>
            <Field label="Zoom passcode">
              <input className="field" placeholder="optional" value={edit.zoom_passcode || ""} onChange={(e) => setEdit({ ...edit, zoom_passcode: e.target.value })} data-testid="live-zoom-passcode"/>
            </Field>
            <Field label="Legacy meeting URL">
              <input className="field" value={edit.meeting_url || ""} onChange={(e) => setEdit({ ...edit, meeting_url: e.target.value })}/>
            </Field>
            <Field label="Join instructions (shown to enrolled candidates)" wide>
              <textarea className="field" rows={3} placeholder="Dial-in details, timezone reminder, workbook link, dress code…"
                        value={edit.join_instructions || ""} onChange={(e) => setEdit({ ...edit, join_instructions: e.target.value })}
                        data-testid="live-join-instructions"/>
            </Field>
          </div>
          <ModalActions onCancel={() => setEdit(null)} onSave={save} saveLabel="Save session"/>
        </Modal>
      )}
      <FieldStyles/>
    </section>
  );
}

/* ========= Comments moderation ========= */
function CommentsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/comments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (c) => {
    if (!window.confirm("Delete this comment and any replies?")) return;
    try { await api.delete(`/comments/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section className="space-y-3" data-testid="admin-comments">
      {rows.map((c) => (
        <article key={c.id} className="card-warm p-4" data-testid={`admin-comment-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[var(--navy-deep)]">{c.user_name} <span className="text-zinc-500 font-normal">· {(c.created_at||"").slice(0,16).replace("T", " ")}</span></div>
              <div className="text-[11px] text-zinc-500">course {c.course_id?.slice(0,8)} / lesson {c.lesson_id?.slice(0,8)} {c.parent_id ? "· reply" : ""}</div>
              <div className="mt-2 text-sm text-zinc-800 whitespace-pre-wrap break-words">{c.body}</div>
            </div>
            <button onClick={() => remove(c)} className="chip inline-flex items-center gap-1 text-xs text-[var(--terracotta)]" data-testid={`delete-comment-${c.id}`}><Trash2 size={12}/></button>
          </div>
        </article>
      ))}
      {rows.length === 0 && <div className="text-sm text-zinc-500 text-center py-10">No comments posted yet.</div>}
    </section>
  );
}

/* ========= CPD entries ========= */
function CPDTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/cpd-entries").then((r) => setRows(r.data)); }, []);
  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return !t ? rows : rows.filter(r => (r.user_name || "").toLowerCase().includes(t) || (r.user_email || "").toLowerCase().includes(t) || (r.title || "").toLowerCase().includes(t));
  }, [q, rows]);
  return (
    <section className="space-y-4" data-testid="admin-cpd">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or activity…" className="w-full pl-9 pr-4 py-2 rounded-full border border-[var(--border-warm)] bg-white text-sm"/>
        </div>
        <span className="text-xs text-zinc-500">{filtered.length} entries</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)]">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-[var(--muted-surface)] text-left">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Designation</th>
              <th className="px-4 py-3">Activity</th>
              <th className="px-4 py-3">Hours</th>
              <th className="px-4 py-3">Title</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border-warm)]" data-testid={`cpd-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-xs">{r.user_name}</div>
                  <div className="text-[10px] text-zinc-500 flex items-center gap-1"><Mail size={10}/> {r.user_email}</div>
                </td>
                <td className="px-4 py-3 text-xs">{(r.activity_date || "").slice(0,10)}</td>
                <td className="px-4 py-3 text-xs">{r.designation_code || "-"}</td>
                <td className="px-4 py-3 text-xs">{r.activity_type || "-"}</td>
                <td className="px-4 py-3 text-xs">{r.hours || 0}</td>
                <td className="px-4 py-3 text-xs">{r.title || "-"}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No CPD entries</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ========= Settings (ClassMarker + integrations) ========= */
function SettingsTab() {
  return (
    <section className="space-y-6" data-testid="admin-settings">
      <div className="card-warm p-6">
        <div className="eyebrow mb-1">Platform</div>
        <h3 className="font-display text-2xl">Native CSI exam engine</h3>
        <p className="text-sm text-zinc-700 mt-2">
          CSI runs its own exam engine - every programme's test, question bank, honour-code signature, grading rubric and transcript is managed entirely inside this admin.
          There are no third-party testing providers to configure. To author or edit any exam go to <strong>Admin → Exams</strong>.
        </p>
        <ul className="list-disc pl-5 text-sm text-zinc-700 mt-3 space-y-1">
          <li><strong>Admin → Exams</strong> - create, clone, grade, download transcripts, bulk-shuffle, rubric templates.</li>
          <li><strong>Admin → Live sessions</strong> - Zoom URL, passcode and join instructions per cohort (CSCE and CSP).</li>
          <li><strong>Admin → Payments</strong> - approve candidates to unlock their exam launcher.</li>
          <li><strong>Admin → Enquiries</strong> - reply to any candidate question routed from the course page.</li>
        </ul>
      </div>
      <FieldStyles/>
    </section>
  );
}

/* ========= Shared primitives ========= */
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} shadow-2xl relative my-8`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="font-display text-xl pr-6">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-[var(--terracotta)]" aria-label="Close"><X size={18}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <div className={`block mb-3 ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[var(--navy-deep)] uppercase tracking-wider mb-1 block">{label}</span>
      {children}
    </div>
  );
}
function ModalActions({ onCancel, onSave, saveLabel, saveTestId }) {
  return (
    <div className="flex gap-2 pt-3 mt-2 border-t border-[var(--border-warm)]">
      <button type="button" onClick={onCancel} className="chip flex-1">Cancel</button>
      <button type="button" onClick={onSave} className="btn-terracotta flex-1 py-2 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-1" data-testid={saveTestId}>
        <Save size={13}/> {saveLabel}
      </button>
    </div>
  );
}
function SkeletonCard() {
  return <div className="h-24 rounded-2xl bg-[var(--muted-surface)] animate-pulse"/>;
}
function FieldStyles() {
  return (
    <style>{`.field{width:100%;background:#fff;border:1px solid var(--border-warm);border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:inherit}.field:focus{border-color:var(--navy)}textarea.field{min-height:72px;resize:vertical}`}</style>
  );
}
